import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, ConfirmDialog } from '../common';
import { useAuthStore } from '../../store/auth.store';
import { Comment as CommentType } from '../../types';
import { CommentForm } from './CommentForm';
import { formatRelativeTime } from '../../utils/formatDate';
import './Comment.css';

interface CommentProps {
  comment: CommentType;
  onEdit: (id: number, text: string) => void;
  onDelete: (id: number) => void;
  onReply: (text: string, parentCommentId: number) => void;
  isEditing: boolean;
  isDeleting: boolean;
  isReplying: boolean;
  isNested?: boolean;
}

/**
 * Comment component for displaying individual comments with reply support
 * Implements Requirements 11.1, 11.2, 11.3, 11.4, 11.5
 */
export const Comment: React.FC<CommentProps> = ({
  comment,
  onEdit,
  onDelete,
  onReply,
  isEditing,
  isDeleting,
  isReplying,
  isNested = false
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedText, setEditedText] = useState(comment.text);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(false);

  const isAuthor = user?.id === comment.authorId;
  const wasEdited = comment.updatedAt !== comment.createdAt;
  const hasReplies = comment.replies && comment.replies.length > 0;

  const handleSaveEdit = () => {
    if (!editedText.trim()) {
      setEditedText(comment.text);
      setIsEditMode(false);
      return;
    }

    if (editedText.trim() !== comment.text) {
      onEdit(comment.id, editedText.trim());
    }
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditedText(comment.text);
    setIsEditMode(false);
  };

  const handleDelete = () => {
    onDelete(comment.id);
    setShowDeleteConfirm(false);
  };

  const handleReply = (text: string) => {
    onReply(text, comment.id);
    setShowReplyForm(false);
    setShowReplies(true); // Automatically show replies after adding one
  };



  return (
    <>
      <div className={`comment ${isNested ? 'comment--reply' : ''}`}>
        <div className="comment__header">
          <div className="comment__author-info">
            <div className="comment__avatar">
              {comment.authorName.charAt(0).toUpperCase()}
            </div>
            <div className="comment__meta">
              <span className="comment__author">{comment.authorName}</span>
              <span className="comment__date">
                {formatRelativeTime(comment.createdAt)}
                {wasEdited && <span className="comment__edited"> ({t('card.edited')})</span>}
              </span>
            </div>
          </div>
          {isAuthor && !isEditMode && (
            <div className="comment__actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditMode(true)}
                disabled={isEditing || isDeleting}
              >
                {t('card.editComment')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isEditing || isDeleting}
              >
                {t('card.deleteComment')}
              </Button>
            </div>
          )}
        </div>

        <div className="comment__body">
          {isEditMode ? (
            <div className="comment__edit">
              <Input
                type="textarea"
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                rows={3}
                autoFocus
                disabled={isEditing}
              />
              <div className="comment__edit-actions">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={isEditing || !editedText.trim()}
                >
                  {isEditing ? t('common.loading') : t('common.save')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={isEditing}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <p className="comment__text">{comment.text}</p>
          )}
        </div>

        {!isEditMode && user && (
          <div className="comment__footer">
            <button
              className="comment__reply-btn"
              onClick={() => setShowReplyForm(!showReplyForm)}
              disabled={isReplying}
            >
              💬 {t('card.reply')}
            </button>
            {hasReplies && (
              <button
                className="comment__view-replies-btn"
                onClick={() => setShowReplies(!showReplies)}
              >
                {showReplies ? '▼' : '▶'} {comment.replies.length} {comment.replies.length === 1 ? t('card.reply') : t('card.replies')}
              </button>
            )}
          </div>
        )}

        {showReplyForm && (
          <div className="comment__reply-form">
            <div className="comment__reply-form-header">
              <span className="comment__reply-form-title">
                {t('card.reply')} {comment.authorName}
              </span>
              <button
                className="comment__reply-form-cancel"
                onClick={() => setShowReplyForm(false)}
              >
                ✕
              </button>
            </div>
            <CommentForm
              onSubmit={handleReply}
              isSubmitting={isReplying}
              placeholder={t('card.writeComment')}
              submitButtonText={t('card.reply')}
            />
          </div>
        )}

        {hasReplies && showReplies && (
          <div className="comment__replies">
            {comment.replies.map((reply) => (
              <Comment
                key={reply.id}
                comment={reply}
                onEdit={onEdit}
                onDelete={onDelete}
                onReply={onReply}
                isEditing={isEditing}
                isDeleting={isDeleting}
                isReplying={isReplying}
                isNested={true}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('card.deleteComment')}
        message={t('card.deleteCommentConfirm')}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmText={t('common.delete')}
        confirmVariant="danger"
      />
    </>
  );
};
